<template>
  <div class="container">
    <div class="header">
      <img class="header-icon" v-bind:src="headerIconUrl" />
      <div class="title">{{ title }}</div>
      <div class="summary" v-if="this.summary">{{ summary }}</div>
      <div class="status" v-if="this.statusString" v-bind:style="statusStyle">
        {{ statusString }}
      </div>
    </div>
    <div id="detail">
      <VerticalScrollLayout v-bind:rows="rows" />
    </div>
  </div>
</template>

<script>
import DetailSpec from '@/map/detail/DetailSpec';
import VerticalScrollLayout from '@/views/ui/verticalScrollLayout/VerticalScrollLayout';
import VSLSectionRow from '@/views/ui/verticalScrollLayout/VSLSectionRow.js';
import VSLLabelRow from '@/views/ui/verticalScrollLayout/VSLLabelRow.js';
import VSLImageGalleryRow from '@/views/ui/verticalScrollLayout/VSLImageGalleryRow.js';
import VSLHtmlRow from '@/views/ui/verticalScrollLayout/VSLHtmlRow.js';
import MapService from '@/service/MapService';
import LangUtils from '@/utils/LangUtils';
import * as tinycolor from 'tinycolor2';

export default {
  name: 'MapElementDetailPanel',

  components: {
    VerticalScrollLayout,
  },

  props: {
    detailSpec: DetailSpec,
  },

  data() {
    return {
      mapService: new MapService(),
      rows: null,
    };
  },

  computed: {
    headerIconUrl() {
      const iconUrl = LangUtils.valueForLanguage(
        this.detailSpec.getHeaderIconUrl(),
        this.$i18n.locale
      );
      return this.mapService.buildUrlToAsset(this.detailSpec.mapBundleId, iconUrl);
    },

    title() {
      if (this.detailSpec) {
        return this.detailSpec.getName();
      } else {
        return '';
      }
    },

    summary() {
      if (this.detailSpec) {
        return this.detailSpec.getSummary();
      } else {
        return null;
      }
    },

    statusStyle() {
      const status = this.detailSpec.mapObjectStatus;
      const template = this.detailSpec.mapObjectStatusTemplate;

      if (status && template) {
        var fontColor = '#000000';
        if (tinycolor(template.color).getBrightness() > 205) {
          fontColor = tinycolor(template.color).darken(100);
        } else {
          fontColor = tinycolor(template.color).lighten(100);
        }
        return 'background-color: ' + template.color + '; color: ' + fontColor;
      } else {
        return '';
      }
    },

    statusString() {
      const status = this.detailSpec.mapObjectStatus;
      const template = this.detailSpec.mapObjectStatusTemplate;

      if (status && status.availableOnMap && template) {
        return LangUtils.valueForLanguage(template.title, this.$i18n.locale);
      } else {
        return null;
      }
    },
  },

  methods: {
    prepareVSLRows() {
      this.rows = [];

      const infos = this.detailSpec.getInfos(
        this.$i18n,
        this.mapService,
        this.detailSpec.mapBundleId
      );
      if (infos && infos.length > 0) {
        var informatinSectionAdded = false;
        for (const info of infos) {
          const value =
            info.value.constructor.name === 'Object'
              ? LangUtils.valueForLanguage(info.value, this.$i18n.locale)
              : info.value;

          if (info.type == 'images') {
            this.rows.push(new VSLImageGalleryRow(info.id, value));
          } else if (info.type == 'section') {
            this.rows.push(new VSLSectionRow(info.id, value));
          } else if (info.type == 'html') {
            this.rows.push(new VSLHtmlRow(info.id, null, value));
          } else {
            if (!informatinSectionAdded) {
              this.rows.push(
                new VSLSectionRow(
                  'information_section',
                  this.$i18n.t('map_detail.detail_panel.information_section')
                )
              );
              informatinSectionAdded = true;
            }
            this.rows.push(
              new VSLLabelRow(
                info.id,
                LangUtils.valueForLanguage(info.label, this.$i18n.locale),
                value,
                info.type
              )
            );
          }
        }
      }

      const description = this.detailSpec.getDescription();
      if (description) {
        this.rows.push(
          new VSLSectionRow(
            'description_section',
            this.$i18n.t('map_detail.detail_panel.description_section')
          )
        );
        this.rows.push(new VSLLabelRow('description', null, description));
      }

      const observation = this.detailSpec.getObservation();
      if (observation) {
        this.rows.push(
          new VSLSectionRow(
            "observation_section",
            this.$i18n.t('map_detail.detail_panel.observation_section')
          )
        );
        this.rows.push(new VSLLabelRow('observation', null, observation));
      }

      if (this.rows.length == 0) {
        this.rows.push(
          new VSLSectionRow(
            'information_section',
            this.$i18n.t('map_detail.detail_panel.information_section')
          )
        );
        this.rows.push(
          new VSLLabelRow(
            'no_information_label',
            null,
            LangUtils.valueForLanguage(this.$i18n.t('map_detail.detail_panel.no_information_label'))
          )
        );
      }
    },

    getCoordinatesLatitude(detailSpec) {
      if (detailSpec) {
        return detailSpec.getCoordinatesLatitude();
      } else {
        return '';
      }
    },

    getCoordinatesLongitude(detailSpec) {
      if (detailSpec) {
        return detailSpec.getCoordinatesLongitude();
      } else {
        return '';
      }
    },
  },

  watch: {
    detailSpec: function () {
      this.prepareVSLRows();
    },
  },

  mounted() {
    this.prepareVSLRows();
  },
};
</script>

<style scoped>
.container {
  overflow: scroll;
  padding: 0px;
}

.header {
  display: grid;
  grid-template-columns: 50px auto;
  grid-template-rows: auto auto auto;
  grid-template-areas: 'header-icon title' 'header-icon summary' 'status status';
  padding: 8px;
  padding-top: 16px;
}

.header-icon {
  grid-area: header-icon;
  width: 40px;
}

.title {
  grid-area: title;
  font-weight: bold;
  font-size: 20px;
  text-align: left;
}

.summary {
  grid-area: summary;
  color: #999999;
  font-size: 12px;
  text-align: left;
}

.status {
  grid-area: status;
  background-color: #ff0000;
  border-radius: 5px;
  padding: 3px 5px;
  font-size: 12px;
}
</style>
